package com.fantasytenis.web.components.bracket

import com.fantasytenis.shared.domain.bracket.entity.BracketMatchEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketRoundEntity
import com.fantasytenis.shared.domain.bracket.entity.RoundName

/**
 * Layout configuration for bracket tree visualization.
 */
object BracketLayoutConfig {
    // Match node dimensions
    const val MATCH_WIDTH = 200
    const val MATCH_HEIGHT = 80
    const val MATCH_HEIGHT_COMPACT = 60

    // Spacing between elements
    const val ROUND_GAP = 160 // Horizontal gap between rounds
    const val MATCH_VERTICAL_GAP = 16 // Minimum vertical gap between matches in first round
    const val CONNECTOR_CURVE_RADIUS = 20

    // Champion box dimensions
    const val CHAMPION_WIDTH = 180
    const val CHAMPION_HEIGHT = 100

    // Padding
    const val CONTAINER_PADDING = 24
}

/**
 * Represents the calculated position of a match in the bracket tree.
 */
data class MatchPosition(
    val matchId: String,
    val round: RoundName,
    val matchNumber: Int,
    val x: Double,
    val y: Double,
    val width: Int = BracketLayoutConfig.MATCH_WIDTH,
    val height: Int = BracketLayoutConfig.MATCH_HEIGHT,
    val isLeftHalf: Boolean, // true for top half (left side), false for bottom half (right side)
    val feedsIntoMatchId: String? = null // The match this winner advances to
)

/**
 * Represents a connector line between two matches.
 */
data class ConnectorLine(
    val fromMatchId: String,
    val toMatchId: String,
    val fromX: Double,
    val fromY: Double,
    val toX: Double,
    val toY: Double,
    val isLeftHalf: Boolean
)

/**
 * Complete bracket layout with all positions and connectors.
 */
data class BracketLayout(
    val positions: List<MatchPosition>,
    val connectors: List<ConnectorLine>,
    val totalWidth: Double,
    val totalHeight: Double,
    val championX: Double,
    val championY: Double
)

/**
 * Calculates the layout for a bracket tree visualization.
 *
 * The bracket is displayed as a traditional tournament tree with:
 * - Early rounds on the outside (left and right edges)
 * - Later rounds moving toward the center
 * - The final/champion in the center
 */
object BracketLayoutCalculator {

    /**
     * Calculate the complete bracket layout for the given rounds.
     *
     * @param rounds List of bracket rounds with matches
     * @param drawSize The tournament draw size (16, 32, 64, 128)
     * @param useCompactHeight Whether to use compact match height
     * @return Complete bracket layout with positions and connectors
     */
    fun calculateLayout(
        rounds: List<BracketRoundEntity>,
        drawSize: Int,
        useCompactHeight: Boolean = false
    ): BracketLayout {
        val matchHeight = if (useCompactHeight) BracketLayoutConfig.MATCH_HEIGHT_COMPACT else BracketLayoutConfig.MATCH_HEIGHT
        val positions = mutableListOf<MatchPosition>()
        val connectors = mutableListOf<ConnectorLine>()

        // Determine rounds and their positions
        val roundNames = RoundName.getRoundsForDrawSize(drawSize)
        val numRounds = roundNames.size
        val halfWidth = (numRounds - 1) * BracketLayoutConfig.ROUND_GAP + BracketLayoutConfig.MATCH_WIDTH

        // Calculate total height based on first round matches
        // Each first-round match gets 2.5x its height in vertical space for good visual separation
        val firstRoundMatches = drawSize / 2 // Total matches in first round
        val firstRoundMatchesInHalf = firstRoundMatches / 2 // Matches per half (used for all vertical positioning)
        val baseSpacingPerMatch = (matchHeight * 2.5).toInt()  // 2.5x height = match + 1.5x whitespace
        val firstRoundHeight = firstRoundMatchesInHalf * baseSpacingPerMatch
        val totalHeight = (firstRoundHeight + BracketLayoutConfig.CONTAINER_PADDING * 2).toDouble()

        // Champion position (center)
        val championX = halfWidth + BracketLayoutConfig.ROUND_GAP / 2.0
        val championY = totalHeight / 2.0 - BracketLayoutConfig.CHAMPION_HEIGHT / 2.0

        // Total width includes both halves plus champion area
        val totalWidth = (halfWidth * 2 + BracketLayoutConfig.ROUND_GAP + BracketLayoutConfig.CONTAINER_PADDING * 2).toDouble()

        // Process each round
        for ((roundIndex, roundName) in roundNames.withIndex()) {
            val roundData = rounds.find { it.name == roundName } ?: continue
            val matchesInRound = roundData.matches.size

            // For all rounds except the final, split into two halves
            if (roundName != RoundName.F) {
                val matchesPerHalfInRound = matchesInRound / 2

                // Left half (top half of draw)
                for (i in 0 until matchesPerHalfInRound) {
                    val match = roundData.matches[i]
                    val position = calculateMatchPosition(
                        match = match,
                        roundIndex = roundIndex,
                        matchIndexInHalf = i,
                        firstRoundMatchesInHalf = firstRoundMatchesInHalf,
                        totalHeight = totalHeight,
                        matchHeight = matchHeight,
                        isLeftHalf = true,
                        numRounds = numRounds,
                        roundNames = roundNames
                    )
                    positions.add(position)
                }

                // Right half (bottom half of draw)
                for (i in 0 until matchesPerHalfInRound) {
                    val match = roundData.matches[matchesPerHalfInRound + i]
                    val position = calculateMatchPosition(
                        match = match,
                        roundIndex = roundIndex,
                        matchIndexInHalf = i,
                        firstRoundMatchesInHalf = firstRoundMatchesInHalf,
                        totalHeight = totalHeight,
                        matchHeight = matchHeight,
                        isLeftHalf = false,
                        numRounds = numRounds,
                        roundNames = roundNames
                    )
                    positions.add(position)
                }
            } else {
                // Final match - center position
                val match = roundData.matches.firstOrNull()
                if (match != null) {
                    positions.add(
                        MatchPosition(
                            matchId = match.id,
                            round = roundName,
                            matchNumber = match.matchNumber,
                            x = championX,
                            y = championY,
                            width = BracketLayoutConfig.CHAMPION_WIDTH,
                            height = BracketLayoutConfig.CHAMPION_HEIGHT,
                            isLeftHalf = true // Doesn't matter for final
                        )
                    )
                }
            }
        }

        // Calculate connector lines
        connectors.addAll(calculateConnectors(positions, roundNames))

        return BracketLayout(
            positions = positions,
            connectors = connectors,
            totalWidth = totalWidth,
            totalHeight = totalHeight,
            championX = championX,
            championY = championY
        )
    }

    private fun calculateMatchPosition(
        match: BracketMatchEntity,
        roundIndex: Int,
        matchIndexInHalf: Int,
        firstRoundMatchesInHalf: Int,
        totalHeight: Double,
        matchHeight: Int,
        isLeftHalf: Boolean,
        numRounds: Int,
        roundNames: List<RoundName>
    ): MatchPosition {
        // X position: left half starts from left edge, right half from right edge
        // Both move toward center with each round
        val x = if (isLeftHalf) {
            BracketLayoutConfig.CONTAINER_PADDING + roundIndex * BracketLayoutConfig.ROUND_GAP.toDouble()
        } else {
            // Right side: mirror from right edge
            val rightEdge = BracketLayoutConfig.CONTAINER_PADDING +
                (numRounds - 1) * BracketLayoutConfig.ROUND_GAP * 2 + BracketLayoutConfig.ROUND_GAP + BracketLayoutConfig.MATCH_WIDTH
            rightEdge - roundIndex * BracketLayoutConfig.ROUND_GAP - BracketLayoutConfig.MATCH_WIDTH.toDouble()
        }

        // Y position: spread matches evenly, with later rounds centered between their feeders
        val verticalSpacing = calculateVerticalSpacing(
            roundIndex = roundIndex,
            matchIndexInHalf = matchIndexInHalf,
            firstRoundMatchesInHalf = firstRoundMatchesInHalf,
            totalHeight = totalHeight,
            matchHeight = matchHeight
        )

        return MatchPosition(
            matchId = match.id,
            round = match.round,
            matchNumber = match.matchNumber,
            x = x,
            y = verticalSpacing,
            height = matchHeight,
            isLeftHalf = isLeftHalf
        )
    }

    /**
     * Calculate the vertical (Y) position for a match.
     *
     * Uses the first round's match count as baseline, then spreads later rounds
     * exponentially so each match is centered between its two feeder matches.
     */
    private fun calculateVerticalSpacing(
        roundIndex: Int,
        matchIndexInHalf: Int,
        firstRoundMatchesInHalf: Int,
        totalHeight: Double,
        matchHeight: Int
    ): Double {
        val usableHeight = totalHeight - BracketLayoutConfig.CONTAINER_PADDING * 2
        // Always base spacing on first round's match count
        val baseSpacing = usableHeight / firstRoundMatchesInHalf

        // Each subsequent round spreads matches 2x further apart
        val spreadFactor = 1 shl roundIndex // 1, 2, 4, 8... for rounds 0, 1, 2, 3...
        val adjustedSpacing = baseSpacing * spreadFactor

        // Center within the space allocated for this match
        return BracketLayoutConfig.CONTAINER_PADDING +
            matchIndexInHalf * adjustedSpacing +
            adjustedSpacing / 2 - matchHeight / 2
    }

    private fun calculateConnectors(
        positions: List<MatchPosition>,
        roundNames: List<RoundName>
    ): List<ConnectorLine> {
        val connectors = mutableListOf<ConnectorLine>()

        // Group positions by round
        val positionsByRound = positions.groupBy { it.round }

        // For each round (except the last), connect to next round
        for (i in 0 until roundNames.size - 1) {
            val currentRound = roundNames[i]
            val nextRound = roundNames[i + 1]

            val currentPositions = positionsByRound[currentRound] ?: continue
            val nextPositions = positionsByRound[nextRound] ?: continue

            // Each pair of matches in current round feeds into one match in next round
            currentPositions.chunked(2).forEachIndexed { index, pair ->
                if (pair.size == 2 && index < nextPositions.size) {
                    val nextMatch = nextPositions[index]

                    pair.forEach { currentMatch ->
                        val fromX = if (currentMatch.isLeftHalf) {
                            currentMatch.x + currentMatch.width
                        } else {
                            currentMatch.x
                        }
                        val fromY = currentMatch.y + currentMatch.height / 2

                        val toX = if (currentMatch.isLeftHalf) {
                            nextMatch.x
                        } else {
                            nextMatch.x + nextMatch.width
                        }
                        val toY = nextMatch.y + nextMatch.height / 2

                        connectors.add(
                            ConnectorLine(
                                fromMatchId = currentMatch.matchId,
                                toMatchId = nextMatch.matchId,
                                fromX = fromX,
                                fromY = fromY,
                                toX = toX,
                                toY = toY,
                                isLeftHalf = currentMatch.isLeftHalf
                            )
                        )
                    }
                }
            }
        }

        return connectors
    }
}

/**
 * Utility to find dependent picks when changing a selection.
 * When a user changes a pick in an earlier round, all later picks
 * that depended on that player need to be cleared.
 */
object CascadingPicksCalculator {

    /**
     * Find all picks that depend on a given player selection.
     * Only returns picks from LATER rounds than the changed position.
     *
     * @param currentPicks Current picks map (positionId -> winnerId)
     * @param changedPositionId The position where the pick is being changed
     * @param previousWinnerId The player who was previously selected
     * @param rounds All bracket rounds
     * @return List of position IDs that need to be cleared
     */
    fun findDependentPicks(
        currentPicks: Map<String, String>,
        changedPositionId: String,
        previousWinnerId: String,
        rounds: List<BracketRoundEntity>
    ): List<String> {
        // Build a map of positionId -> round for quick lookup
        val positionToRound = mutableMapOf<String, RoundName>()
        rounds.forEach { round ->
            round.matches.forEach { match ->
                positionToRound[match.player1.id] = round.name
                positionToRound[match.player2.id] = round.name
            }
        }

        // Find the round of the changed position
        val changedRound = positionToRound[changedPositionId] ?: return emptyList()
        val changedRoundIndex = getRoundIndex(changedRound)

        val dependentPicks = mutableListOf<String>()

        // Find picks in LATER rounds where this player was picked
        currentPicks.forEach { (positionId, winnerId) ->
            if (winnerId == previousWinnerId && positionId != changedPositionId) {
                val pickRound = positionToRound[positionId]
                if (pickRound != null) {
                    val pickRoundIndex = getRoundIndex(pickRound)
                    // Only include if the pick is in a later round
                    if (pickRoundIndex > changedRoundIndex) {
                        dependentPicks.add(positionId)
                    }
                }
            }
        }

        return dependentPicks
    }

    /**
     * Get the index of a round (higher = later in tournament)
     */
    private fun getRoundIndex(round: RoundName): Int {
        return when (round) {
            RoundName.R128 -> 0
            RoundName.R64 -> 1
            RoundName.R32 -> 2
            RoundName.R16 -> 3
            RoundName.QF -> 4
            RoundName.SF -> 5
            RoundName.F -> 6
        }
    }
}
